import { useEffect, useState } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
} from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { SignIn } from './components/SignIn';
import { AccessDenied } from './components/AccessDenied';
import { ConfirmModal } from './components/ConfirmModal';
import { UserManagement } from './components/UserManagement';
import { JobSitesManagement } from './components/JobSitesManagement';
import { ViewInspections } from './components/ViewInspections';
import { InspectionReportViewer } from './components/InspectionReportViewer';
import { ManagerReportReview } from './components/ManagerReportReview';
import { ReportSetup } from './components/ReportSetup';
import { PropertyDeviceManagement } from './components/PropertyDeviceManagement';
import { InspectionReportWorkflow } from './components/InspectionReportWorkflow';
import { IntegrationSettings } from './components/IntegrationSettings';
import { ThemeManagement } from './components/ThemeManagement';
import { NotificationManagement } from './components/NotificationManagement';
import { WaterRestrictionSchedulesManagement } from './components/WaterRestrictionSchedulesManagement';
import { GpsDiagnostics } from './components/GpsDiagnostics';
import { JobCodesManagement } from './components/JobCodesManagement';
import { ReportStatusSettings } from './components/ReportStatusSettings';
import { ControllerSettings } from './components/ControllerSettings';
import { FeedbackManagement } from './components/FeedbackManagement';
import { FeedbackModal } from './components/FeedbackModal';
import { AuditLogViewer } from './components/AuditLogViewer';
import { EventLogViewer } from './components/EventLogViewer';
import { InspectionsDashboard } from './components/InspectionsDashboard';
import { ManagerDashboard } from './components/ManagerDashboard';
import { ComplianceReport } from './components/ComplianceReport';
import { InspectionCompletionReport } from './components/InspectionCompletionReport';
import { WateringSchedulesReport } from './components/WateringSchedulesReport';
import { Layout } from './components/Layout';
import i18n from './i18n/config';
import { apiClient } from './lib/apiClient';
import { loadProfile, type ProfileResult, type UserProfile } from './lib/auth';
import { SIGNIN_DESIGN_STORAGE_KEY } from './components/SignIn';
import { installGlobalErrorHandlers, logEvent } from './lib/telemetry';
import { startSyncTriggers } from './lib/inspectionSync';
import { requestPersistentStorage } from './lib/idb';
import {
  peekReturnLocation,
  clearReturnLocation,
  setCurrentLocation,
  onReauthRequired,
  startTokenRefreshOnFocus,
  redirectToLogin,
  releaseReauth,
  type ReturnLocation,
} from './lib/authSession';

type View =
  | 'dashboard'
  | 'compliance-report'
  | 'inspection-completion'
  | 'watering-schedules'
  | 'users'
  | 'job-sites'
  | 'view-inspections'
  | 'view-report'
  | 'review-report'
  | 'inspections-dashboard'
  | 'start-inspection'
  | 'manage-devices'
  | 'inspection-form'
  | 'integrations'
  | 'theme-management'
  | 'notifications'
  | 'water-restrictions'
  | 'job-codes'
  | 'report-status'
  | 'controller-settings'
  | 'feedback-management'
  | 'audit-log'
  | 'event-log'
  | 'gps-diagnostics';

interface InspectionContext {
  jobSiteId: string;
  jobSiteName: string;
  reportId?: string;
  aspireWorkTicketId?: number | null;
  aspireWorkTicketNumber?: number | null;
}

// Views we can safely restore a user to after a re-auth redirect. Excludes
// 'inspection-form', which needs an in-memory InspectionContext we don't persist
// (the tech's draft is preserved in IndexedDB; they re-enter it from the setup
// screen). Report detail views are only restored when we also have the id.
const RESTORABLE_VIEWS = new Set<View>([
  'dashboard', 'compliance-report', 'inspection-completion', 'watering-schedules', 'users', 'job-sites',
  'view-inspections', 'view-report', 'review-report', 'inspections-dashboard',
  'start-inspection', 'manage-devices', 'integrations', 'theme-management',
  'notifications', 'water-restrictions', 'job-codes', 'report-status',
  'controller-settings', 'feedback-management', 'audit-log', 'event-log', 'gps-diagnostics',
]);

function sanitizeRestore(r: ReturnLocation | null): { view: View; activeReportId: string | null } | null {
  if (!r) return null;
  const v = r.view as View;
  if (!RESTORABLE_VIEWS.has(v)) return null;
  if ((v === 'view-report' || v === 'review-report') && !r.activeReportId) return null;
  return { view: v, activeReportId: r.activeReportId };
}

function AuthorizedShell() {
  const [state, setState] = useState<ProfileResult | { status: 'loading' } | { status: 'error'; message: string }>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await loadProfile();
        if (!cancelled) {
          // Apply the saved locale before first render so headings render in
          // the right language. Falls through to LanguageDetector's value if
          // the profile has no preference saved.
          if (result.status === 'authorized' && result.profile.preferredLocale) {
            const lang = result.profile.preferredLocale;
            if (i18n.language !== lang) {
              await i18n.changeLanguage(lang);
            }
          }
          // Mirror the saved sign-in design preference to localStorage so the
          // pre-auth SignIn screen can honor it on the user's next sign-out.
          if (result.status === 'authorized') {
            const pref = result.profile.signinDesign ?? 'random';
            try {
              localStorage.setItem(SIGNIN_DESIGN_STORAGE_KEY, pref);
            } catch {
              /* localStorage unavailable (private mode, quota) — best-effort only */
            }
          }
          // Log the login to the activity log, once per browser-tab session
          // (sessionStorage clears on tab close, so the next sign-in re-logs).
          // Fire-and-forget — logging must never block sign-in.
          if (
            result.status === 'authorized' &&
            typeof sessionStorage !== 'undefined' &&
            !sessionStorage.getItem('login_logged')
          ) {
            sessionStorage.setItem('login_logged', '1');
            void apiClient.post('/api/auth/login').catch((err) => {
              console.error('[App] login audit failed:', err);
            });
            // Telemetry breadcrumb: one session_start per tab, with role + locale
            // + viewport. Lets us tie subsequent breadcrumbs to a sign-in.
            void logEvent('client_session_start', `Authorized session started`, {
              metadata: {
                role: result.profile.role,
                locale: result.profile.preferredLocale ?? i18n.language ?? null,
                viewport_width: typeof window !== 'undefined' ? window.innerWidth : null,
                viewport_height: typeof window !== 'undefined' ? window.innerHeight : null,
                online: typeof navigator !== 'undefined' ? navigator.onLine : null,
              },
            });
          }
          setState(result);
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') return <CenteredMessage text="Loading…" />;
  if (state.status === 'error') return <CenteredMessage text={`Error: ${state.message}`} />;
  if (state.status === 'access_denied') {
    return <AccessDenied message={state.message} />;
  }
  if (state.status === 'unauthorized') {
    return <CenteredMessage text="Session expired. Please refresh." />;
  }

  return <Shell profile={state.profile} />;
}

function Shell({ profile }: { profile: UserProfile }) {
  const { t } = useTranslation('common');

  // Offline resilience: once a tech is signed in, wire the background sync
  // triggers (online event + periodic + startup flush) so any inspection work
  // saved on this device while offline uploads as soon as connectivity returns,
  // and ask the browser for persistent storage so Safari's ITP eviction can't
  // drop a queued draft after a few days of disuse. Both are fail-soft no-ops
  // when IndexedDB is unavailable.
  useEffect(() => {
    startSyncTriggers();
    void requestPersistentStorage();
  }, []);

  // Manager/Admin land on the analytics Dashboard; field techs (User) start on
  // the inspection setup screen and have no access to the Dashboard at all.
  const isManagerPlus = profile.role === 'Manager' || profile.role === 'Admin';

  // If we're booting straight after a re-auth redirect, land the user back on the
  // screen they left (validated + account-matched inside authSession). Read once
  // during init (a pure peek — no side effects, StrictMode-safe); the breadcrumb
  // is cleared in an effect below.
  const [restored] = useState(() => sanitizeRestore(peekReturnLocation()));
  const [view, setView] = useState<View>(restored?.view ?? (isManagerPlus ? 'dashboard' : 'start-inspection'));
  const [activeReportId, setActiveReportId] = useState<string | null>(restored?.activeReportId ?? null);
  const [inspectionCtx, setInspectionCtx] = useState<InspectionContext | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [showReauth, setShowReauth] = useState(false);

  // Consume the one-time return breadcrumb read during state init.
  useEffect(() => {
    clearReturnLocation();
  }, []);

  // Keep the session layer aware of where the user is, so if a token renewal
  // forces an interactive sign-in we can bring them back to this exact screen.
  useEffect(() => {
    setCurrentLocation(view, activeReportId);
  }, [view, activeReportId]);

  // Session continuity: when a silent renewal fails, show a "sign in to continue"
  // modal (redirect on tap) instead of an abrupt bounce; and silently refresh the
  // token whenever the tab regains focus so the next action is less likely to
  // trigger a redirect at all.
  useEffect(() => {
    const offReauth = onReauthRequired(() => setShowReauth(true));
    const stopFocusRefresh = startTokenRefreshOnFocus();
    return () => {
      offReauth();
      stopFocusRefresh();
    };
  }, []);

  // Signal can drop between the prompt appearing and the user tapping it. In
  // that case redirectToLogin declines to navigate (the Microsoft sign-in page
  // can't load offline), so dismiss instead of leaving a button that does
  // nothing — the tech keeps working, the inspection keeps saving to the device,
  // and the next request once signal returns prompts again.
  const handleReauthConfirm = async () => {
    const navigated = await redirectToLogin();
    if (!navigated) {
      releaseReauth();
      setShowReauth(false);
    }
  };

  const handleNavigate = (page: string) => {
    if (page === 'dashboard' && isManagerPlus) {
      setView('dashboard');
    } else if (page === 'compliance-report' && isManagerPlus) {
      setView('compliance-report');
    } else if (page === 'inspection-completion' && isManagerPlus) {
      setView('inspection-completion');
    } else if (page === 'watering-schedules' && isManagerPlus) {
      setView('watering-schedules');
    } else if (page === 'user-management' && profile.role === 'Admin') {
      setView('users');
    } else if (page === 'job-sites' && isManagerPlus) {
      setView('job-sites');
    } else if (page === 'view-inspections') {
      setView('view-inspections');
    } else if (page === 'inspections-dashboard' && (profile.role === 'Manager' || profile.role === 'Admin')) {
      setView('inspections-dashboard');
    } else if (page === 'start-inspection') {
      setInspectionCtx(null);
      setView('start-inspection');
    } else if (page === 'manage-devices') {
      // Available to all roles (field techs included) — standalone device
      // setup outside an inspection report.
      setView('manage-devices');
    } else if (page === 'integrations' && profile.role === 'Admin') {
      setView('integrations');
    } else if (page === 'theme-management' && profile.role === 'Admin') {
      setView('theme-management');
    } else if (page === 'notifications' && profile.role === 'Admin') {
      setView('notifications');
    } else if (page === 'water-restrictions' && profile.role === 'Admin') {
      setView('water-restrictions');
    } else if (page === 'job-codes' && profile.role === 'Admin') {
      setView('job-codes');
    } else if (page === 'report-status' && profile.role === 'Admin') {
      setView('report-status');
    } else if (page === 'controller-settings' && profile.role === 'Admin') {
      setView('controller-settings');
    } else if (page === 'feedback-management') {
      // Open to every role. WHAT each person sees is scoped server-side in
      // listFeedback/getFeedback, not here — a technician sees only their own.
      setView('feedback-management');
    } else if (page === 'audit-log' && profile.role === 'Admin') {
      setView('audit-log');
    } else if (page === 'event-log' && profile.role === 'Admin') {
      setView('event-log');
    } else if (page === 'gps-diagnostics' && profile.role === 'Admin') {
      setView('gps-diagnostics');
    } else {
      console.warn(`[App] Navigation to "${page}" is not yet implemented in M7.`);
    }
  };

  const handleViewReport = (reportId: string) => {
    setActiveReportId(reportId);
    setView('view-report');
  };

  const handleReviewReport = (reportId: string) => {
    setActiveReportId(reportId);
    setView('review-report');
  };

  const handleBackFromReport = () => {
    setActiveReportId(null);
    setView('view-inspections');
  };

  const handleEditReport = async (reportId: string) => {
    // Fetch the report row so we know which job site it belongs to.
    try {
      const report = await apiClient.get<{ id: string; job_site_id: string | null; job_site_name: string }>(
        `/api/reports/${reportId}`,
      );
      if (!report.job_site_id) {
        console.warn('[App] Cannot edit report without job_site_id');
        return;
      }
      setInspectionCtx({
        jobSiteId: report.job_site_id,
        jobSiteName: report.job_site_name,
        reportId: report.id,
      });
      setView('inspection-form');
    } catch (err) {
      console.error('[App] Failed to load report for edit:', err);
    }
  };

  const handleStartInspection = (
    setup: {
      jobSiteId: string;
      jobSiteName: string;
      aspireWorkTicketId?: number | null;
      aspireWorkTicketNumber?: number | null;
    },
    draftData?: { reportId: string },
  ) => {
    setInspectionCtx({
      jobSiteId: setup.jobSiteId,
      jobSiteName: setup.jobSiteName,
      reportId: draftData?.reportId,
      aspireWorkTicketId: setup.aspireWorkTicketId ?? null,
      aspireWorkTicketNumber: setup.aspireWorkTicketNumber ?? null,
    });
    setView('inspection-form');
  };

  const handleBackFromWorkflow = () => {
    setInspectionCtx(null);
    setView('view-inspections');
  };

  const currentPage = view === 'dashboard'
    ? 'dashboard'
    : view === 'compliance-report'
    ? 'compliance-report'
    : view === 'inspection-completion'
    ? 'inspection-completion'
    : view === 'watering-schedules'
    ? 'watering-schedules'
    : view === 'users'
    ? 'user-management'
    : view === 'job-sites'
      ? 'job-sites'
      : view === 'view-inspections' || view === 'view-report' || view === 'review-report'
        ? 'view-inspections'
        : view === 'inspections-dashboard'
          ? 'inspections-dashboard'
          : view === 'start-inspection' || view === 'inspection-form'
          ? 'start-inspection'
          : view === 'manage-devices'
          ? 'manage-devices'
          : view === 'integrations'
            ? 'integrations'
            : view === 'theme-management'
              ? 'theme-management'
              : view === 'notifications'
                ? 'notifications'
              : view === 'water-restrictions'
                ? 'water-restrictions'
              : view === 'job-codes'
                ? 'job-codes'
                : view === 'report-status'
                  ? 'report-status'
                  : view === 'controller-settings'
                    ? 'controller-settings'
                    : view === 'feedback-management'
                      ? 'feedback-management'
                      : view === 'audit-log'
                        ? 'audit-log'
                        : view === 'event-log'
                          ? 'event-log'
                          : view === 'gps-diagnostics'
                            ? 'gps-diagnostics'
                            : 'start-inspection';

  return (
    <Layout
      userId={profile.id}
      userEmail={profile.email}
      userRole={profile.role}
      userThemeSlug={profile.preferredThemeSlug}
      currentPage={currentPage}
      onNavigate={handleNavigate}
      onOpenFeedback={() => setFeedbackOpen(true)}
    >
      {view === 'dashboard' && isManagerPlus && (
        <ManagerDashboard onReviewReport={handleReviewReport} />
      )}
      {view === 'compliance-report' && isManagerPlus && <ComplianceReport />}
      {view === 'inspection-completion' && isManagerPlus && <InspectionCompletionReport />}
      {view === 'watering-schedules' && isManagerPlus && <WateringSchedulesReport />}
      {view === 'users' && profile.role === 'Admin' && <UserManagement />}
      {view === 'job-sites' && isManagerPlus && <JobSitesManagement readOnly={profile.role !== 'Admin'} />}
      {view === 'view-inspections' && (
        <ViewInspections
          onViewReport={handleViewReport}
          onEditReport={handleEditReport}
          onReviewReport={
            profile.role === 'Manager' || profile.role === 'Admin' ? handleReviewReport : undefined
          }
        />
      )}
      {view === 'view-report' && activeReportId && (
        <InspectionReportViewer reportId={activeReportId} onBack={handleBackFromReport} />
      )}
      {view === 'review-report' && activeReportId && (profile.role === 'Manager' || profile.role === 'Admin') && (
        <ManagerReportReview reportId={activeReportId} onBack={handleBackFromReport} />
      )}
      {view === 'inspections-dashboard' && (profile.role === 'Manager' || profile.role === 'Admin') && (
        <InspectionsDashboard />
      )}
      {view === 'start-inspection' && (
        <ReportSetup onStartInspection={handleStartInspection} />
      )}
      {view === 'manage-devices' && <PropertyDeviceManagement />}
      {view === 'inspection-form' && inspectionCtx && (
        <InspectionReportWorkflow
          jobSiteId={inspectionCtx.jobSiteId}
          jobSiteName={inspectionCtx.jobSiteName}
          reportId={inspectionCtx.reportId}
          aspireWorkTicketId={inspectionCtx.aspireWorkTicketId}
          aspireWorkTicketNumber={inspectionCtx.aspireWorkTicketNumber}
          onBack={handleBackFromWorkflow}
        />
      )}
      {view === 'integrations' && profile.role === 'Admin' && <IntegrationSettings />}
      {view === 'theme-management' && profile.role === 'Admin' && <ThemeManagement />}
      {view === 'notifications' && profile.role === 'Admin' && <NotificationManagement />}
      {view === 'water-restrictions' && profile.role === 'Admin' && <WaterRestrictionSchedulesManagement />}
      {view === 'job-codes' && profile.role === 'Admin' && <JobCodesManagement />}
      {view === 'report-status' && profile.role === 'Admin' && <ReportStatusSettings />}
      {view === 'controller-settings' && profile.role === 'Admin' && <ControllerSettings />}
      {view === 'feedback-management' && <FeedbackManagement />}
      {view === 'audit-log' && profile.role === 'Admin' && <AuditLogViewer />}
      {view === 'event-log' && profile.role === 'Admin' && <EventLogViewer />}
      {view === 'gps-diagnostics' && profile.role === 'Admin' && <GpsDiagnostics />}

      <FeedbackModal
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        currentPage={currentPage}
      />

      <ConfirmModal
        isOpen={showReauth}
        title={t('session.expiredTitle')}
        message={t('session.expiredMessage')}
        confirmText={t('session.signIn')}
        variant="warning"
        hideCancel
        onConfirm={() => {
          void handleReauthConfirm();
        }}
        onCancel={() => {
          void handleReauthConfirm();
        }}
      />
    </Layout>
  );
}

function CenteredMessage({ text }: { text: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-gray-700">
      <p>{text}</p>
    </div>
  );
}

// Install global error handlers once at module load -- they cover both the
// pre-auth SignIn screen and the authenticated shell.
let __handlersInstalled = false;
if (!__handlersInstalled) {
  __handlersInstalled = true;
  installGlobalErrorHandlers();
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  return (
    <>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>{isAuthenticated ? <AuthorizedShell /> : null}</AuthenticatedTemplate>
    </>
  );
}
